import { useState, useRef } from "react";
import { Palette, Loader2, Save, Upload, Copy, ExternalLink, Trash2, Sun, Moon, Wand2 } from "lucide-react";
import { apiFetch, uploadFile } from "@/portal/api";
import { toast } from "sonner";
import { brandLogoSrc, DEFAULT_PARTNER_BRAND } from "@/flotila/brand";

const PRESETS = ["#e30613", "#0f766e", "#1d4ed8", "#111827", "#15803d", "#7c3aed", "#f97316", "#0ea5e9"];

const PartnerBrandingEditor = ({ partner, token, onSaved }) => {
  const b = partner.branding || {};
  const [form, setForm] = useState({
    slug: b.slug || "", company_name: b.company_name || partner.company || partner.name || "",
    primary_color: b.primary_color || DEFAULT_PARTNER_BRAND.primary_color, theme: b.theme || DEFAULT_PARTNER_BRAND.theme,
    tagline: b.tagline || "", website: b.website || "", logo_url: b.logo_url && !b.logo_url.startsWith("/api") ? b.logo_url : "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const fileRef = useRef(null);
  const link = `${window.location.origin}/flotila/${form.slug || "…"}`;
  const logo = brandLogoSrc(b);

  const suggestFromWeb = async () => {
    const site = form.website.trim();
    if (!site) { toast.error("Nejdřív zadejte web partnera."); return; }
    setSuggesting(true);
    try {
      const s = await apiFetch(`/flotila/brand/suggest?url=${encodeURIComponent(site)}`, { token });
      setForm((f) => ({
        ...f,
        company_name: s.company_name || f.company_name,
        primary_color: (s.primary_color && /^#[0-9a-fA-F]{6}$/.test(s.primary_color)) ? s.primary_color : f.primary_color,
        logo_url: s.logo_url || f.logo_url,
        website: s.website || f.website,
      }));
      toast.success("Návrh z webu načten – zkontrolujte a uložte.");
    } catch (e) { toast.error(e.message); } finally { setSuggesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/flotila/partners/${partner.id}/branding`, { method: "PUT", token, body: form });
      toast.success("Design partnera uložen."); onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try { await uploadFile(`/flotila/partners/${partner.id}/logo`, f, token); toast.success("Logo nahráno."); onSaved?.(); }
    catch (e) { toast.error(e.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeLogo = async () => {
    try { await apiFetch(`/flotila/partners/${partner.id}/logo`, { method: "DELETE", token }); onSaved?.(); } catch (e) { toast.error(e.message); }
  };
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Odkaz zkopírován."); };

  return (
    <div className="mt-3 rounded-xl fl-surface-2 border fl-border p-4 space-y-3" data-testid={`partner-branding-${partner.id}`}>
      <p className="text-xs font-semibold inline-flex items-center gap-1.5 fl-accent-text"><Palette className="w-3.5 h-3.5" /> Design a odkaz partnera</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Adresa portálu (slug)</span>
          <div className="flex items-center gap-1 text-sm"><span className="fl-muted shrink-0">/flotila/</span><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="fl-input" placeholder="araver" data-testid="brand-slug" /></div></label>
        <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Název firmy (v hlavičce)</span><input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="fl-input" data-testid="brand-company" /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Podtitulek</span><input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="fl-input" placeholder="Přehled vozů ve správě" /></label>
        <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Web partnera</span>
          <div className="flex items-center gap-2">
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="fl-input flex-1" placeholder="https://www.araver.cz" data-testid="brand-website" />
            <button type="button" onClick={suggestFromWeb} disabled={suggesting} className="fl-btn text-xs whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="brand-suggest-btn" title="Načte název, logo a barvu z webu partnera">{suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Načíst z webu</button>
          </div>
          <span className="text-[10px] fl-muted block mt-1">Vyplní název, logo a hlavní barvu podle webu partnera. Pak jen zkontrolujte a uložte.</span></label>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Hlavní barva</span>
          <div className="flex items-center gap-1.5">
            {PRESETS.map((c) => <button key={c} onClick={() => setForm({ ...form, primary_color: c })} className={`w-6 h-6 rounded-full border-2 ${form.primary_color === c ? "border-white ring-1 ring-black/30" : "border-transparent"}`} style={{ background: c }} title={c} />)}
            <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-8 h-7 rounded cursor-pointer bg-transparent border-0" data-testid="brand-color" />
            <span className="text-xs font-mono fl-muted">{form.primary_color}</span>
          </div>
        </div>
        <div>
          <span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Vzhled</span>
          <div className="inline-flex rounded-full border fl-border p-0.5">
            {[["light", Sun, "Světlý"], ["dark", Moon, "Tmavý"]].map(([k, Icon, l]) => <button key={k} onClick={() => setForm({ ...form, theme: k })} className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1 ${form.theme === k ? "fl-accent-bg" : "fl-muted"}`} data-testid={`brand-theme-${k}`}><Icon className="w-3 h-3" /> {l}</button>)}
          </div>
        </div>
        <div>
          <span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Logo</span>
          <div className="flex items-center gap-2">
            {logo && <img src={logo} alt="" className="h-8 max-w-[120px] object-contain rounded bg-white px-1" />}
            <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="brand-logo-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="fl-btn-ghost text-xs" data-testid="brand-logo-upload">{uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Nahrát</button>
            {b.logo_url?.startsWith("/api") && <button onClick={removeLogo} className="fl-muted hover:text-red-500" title="Odebrat logo"><Trash2 className="w-3.5 h-3.5" /></button>}
            <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="fl-input text-xs max-w-[220px]" placeholder="…nebo URL loga" />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="text-xs fl-muted inline-flex items-center gap-2 flex-wrap">
          <span>Odkaz pro partnera:</span><code className="fl-text-2">{link}</code>
          <button onClick={copy} className="fl-btn-ghost text-xs py-1 px-2" data-testid="brand-copy-link"><Copy className="w-3 h-3" /> Kopírovat</button>
          {form.slug && <a href={`/flotila/${form.slug}`} target="_blank" rel="noreferrer" className="fl-btn-ghost text-xs py-1 px-2" data-testid="brand-preview-link"><ExternalLink className="w-3 h-3" /> Náhled</a>}
        </div>
        <button onClick={save} disabled={saving} className="fl-btn text-xs" data-testid="brand-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Uložit design</button>
      </div>
    </div>
  );
};

export default PartnerBrandingEditor;
